"use client";

import { useState } from "react";
import Link from "@/components/locale-link";
import {
  Checkbox,
  CopyButton,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  HINT,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { renderSlots } from "@/components/tools/template-slots";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { SIGNAL_STANCES } from "@/lib/tool-kit/essay/signal-phrase";
import {
  buildSandwiches,
  CHARACTER_FRAMES,
  CHARACTER_VERBS,
  EXPLAIN_FRAMES,
  explanationWords,
  lengthAdvice,
  quoteWords,
  SANDWICH_MODES,
  SANDWICH_STYLES,
  type Sandwich,
  type SandwichInput,
} from "@/lib/tool-kit/lit-essays/quote-sandwich";

/** English strings. A translated content file overrides these through `ui`. */
export const QUOTATION_SANDWICH_GENERATOR_STRINGS = {
  tryExample: "Try an example",
  clear: "Clear",
  styleLabel: "Citation style",
  styles: { mla: "MLA 9", apa: "APA 7" },
  modeLabel: "Whose words are you quoting?",
  modes: {
    author: "An author or critic (nonfiction, criticism)",
    character: "A character or speaker (fiction, drama, poetry)",
  },
  sourceHeading: "The source",
  authorsLabel: "Author — one name per line",
  authorsPlaceholder: "e.g. Mary Shelley",
  authorsHint:
    "The citation needs the author even when a character is speaking.",
  yearLabel: "Year of publication",
  yearPlaceholder: "e.g. 1818",
  pageLabel: "Page or other locator",
  pagePlaceholder: "e.g. 23, or ch. 20",
  pageHint:
    "Use the page, chapter, or section numbers of your own edition — they differ between editions. Leave it blank for a source with no numbers, and the citation will omit it.",
  quoteHeading: "The quotation",
  quoteLabel: "The exact words you are quoting",
  quotePlaceholder:
    "Copy the words exactly. You can leave off the outer quotation marks.",
  speakerLabel: "Who is speaking",
  speakerPlaceholder: "e.g. the creature",
  verbLabel: "What the speaker does",
  characterVerbs: CHARACTER_VERBS,
  stanceLabel: "Introducing verb",
  stances: {
    neutral: "Neutral — notes, explains, writes",
    argue: "Argues — claims, maintains, contends",
    agree: "Agrees — confirms, supports",
    disagree: "Disagrees — questions, challenges",
    tentative: "Tentative — suggests, implies",
  },
  leadInLabel: "Set the scene — optional first sentence",
  leadInPlaceholder:
    "Say where the quotation comes from in the story or argument.",
  explainHeading: "Your explanation",
  explainIntro:
    "Fill in what you can. Each answer becomes a sentence; a field left blank drops its sentence.",
  claimLabel: "The claim this supports",
  claimPlaceholder: "e.g. the creature’s violence grows out of his isolation",
  meaningLabel: "What the quotation shows",
  meaningPlaceholder:
    "e.g. the creature has turned his isolation into strength",
  keywordLabel: "A key word or phrase to unpack",
  keywordPlaceholder: "e.g. fearless",
  suggestsLabel: "What that word suggests",
  suggestsPlaceholder:
    "e.g. that he has nothing left to lose, so threats no longer control him",
  whyLabel: "Why it matters",
  whyPlaceholder: "e.g. it marks the point where he becomes Victor’s adversary",
  needQuote: "Enter the quotation to see the sandwich.",
  needAuthor: "Add the author to build the introduction and the citation.",
  resultHeading: "Your quote sandwich",
  resultIntro:
    "The same quotation introduced in different ways — honest templates, no AI. Pick one and revise the wording so it sounds like you.",
  versionLabel: "Version {n}",
  showLabels: "Show what each part does",
  partLabels: {
    introduce: "Introduce",
    quote: "Quote and cite",
    explain: "Explain",
  },
  noExplanation: "Add at least one explanation field.",
  lengthHeading: "Length check",
  quoteWordsLabel: "Words in the quotation",
  explainWordsLabel: "Words in your explanation",
  explainRule:
    "A common rule of thumb: explain a quotation in at least as many words as you quote.",
  explainShort:
    "Your explanation is shorter than the quotation — add more analysis.",
  adviceTrim:
    "A long quotation is harder to explain than to quote. Trim it to the words you will analyze.",
  adviceBlock: {
    mla: "In MLA, more than four lines of prose goes in a block quotation, without quotation marks. At roughly this length, check your line count.",
    apa: "In APA, a quotation of 40 words or more goes in a block quotation, without quotation marks.",
  },
  blockLinkSentence: "Format it with the {link}.",
  blockLink: "Block Quote Formatter",
  copy: "Copy",
  copied: "Copied!",
  copyAll: "Copy the paragraph",
  signal: {
    according: "According to",
    as: "As",
    noDate: "n.d.",
    pageBlank: "p. ___",
    blank: "___",
    openQuote: "“",
    closeQuote: "”",
    ellipsis: "…",
  },
  characterFrames: CHARACTER_FRAMES,
  explainFrames: EXPLAIN_FRAMES,
  example: {
    style: "mla",
    mode: "character",
    stance: "neutral",
    authors: "Mary Shelley",
    year: "",
    page: "ch. 20",
    quote: "Beware; for I am fearless, and therefore powerful.",
    speaker: "the creature",
    verb: "warns",
    leadIn:
      "After Victor destroys the half-finished companion he promised, the creature abandons pleading and begins to threaten him.",
    claim:
      "the creature’s violence grows out of his isolation and Victor’s rejection rather than any natural malice",
    meaning: "the creature has turned his isolation into a source of strength",
    keyword: "fearless",
    suggests:
      "that he has nothing left to lose, so Victor’s threats can no longer control him",
    why: "it marks the point where the creature stops being Victor’s victim and becomes his adversary",
  },
} as const;

type Strings = typeof QUOTATION_SANDWICH_GENERATOR_STRINGS;

const EMPTY_INPUT: SandwichInput = {
  style: "mla",
  mode: "author",
  stance: "neutral",
  authors: "",
  year: "",
  page: "",
  quote: "",
  speaker: "",
  verb: "says",
  leadIn: "",
  claim: "",
  meaning: "",
  keyword: "",
  suggests: "",
  why: "",
};

const BADGE =
  "inline-flex h-fit w-fit rounded-md bg-primary/10 px-2 py-0.5 font-medium text-primary text-xs";

function SandwichCard({
  sandwich,
  index,
  showLabels,
  strings,
}: {
  sandwich: Sandwich;
  index: number;
  showLabels: boolean;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const rows = [
    { part: "introduce", text: sandwich.introduce },
    { part: "quote", text: sandwich.quote },
    { part: "explain", text: sandwich.explain },
  ] as const;
  return (
    <div className={`${RESULT_CARD} space-y-3`}>
      <div className="flex items-center justify-between gap-3">
        <h3 className={WIDGET_H3}>
          {t(strings.versionLabel, { n: fmt(index + 1) })}
        </h3>
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyAll}
          small
          text={sandwich.full}
        />
      </div>
      {showLabels ? (
        <ol className="space-y-2">
          {rows
            .filter((row) => row.text !== "")
            .map((row) => (
              <li
                className="grid gap-1 sm:grid-cols-[8.5rem_1fr] sm:gap-3"
                key={row.part}
              >
                <span className={BADGE}>{strings.partLabels[row.part]}</span>
                <span className="text-foreground text-sm leading-relaxed">
                  {row.text}
                </span>
              </li>
            ))}
        </ol>
      ) : (
        <p className="text-foreground text-sm leading-relaxed">
          {sandwich.full}
        </p>
      )}
    </div>
  );
}

function LengthCheck({
  input,
  strings,
}: {
  input: SandwichInput;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const quoted = quoteWords(input);
  const explained = explanationWords(input);
  const advice = lengthAdvice(input);
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.lengthHeading}</h3>
      <StatGrid>
        <StatCard label={strings.quoteWordsLabel} value={fmt(quoted)} />
        <StatCard
          emphasis
          label={strings.explainWordsLabel}
          value={fmt(explained)}
        />
      </StatGrid>
      <p className={HINT}>{strings.explainRule}</p>
      {explained > 0 && explained < quoted ? (
        <Notice>{strings.explainShort}</Notice>
      ) : null}
      {advice === "trim" ? <Notice>{strings.adviceTrim}</Notice> : null}
      {advice === "block" ? (
        <Notice>
          {strings.adviceBlock[input.style]}{" "}
          {renderSlots(strings.blockLinkSentence, {
            link: (
              <Link
                className="text-primary underline underline-offset-4"
                href="/tools/block-quote-formatter"
              >
                {strings.blockLink}
              </Link>
            ),
          })}
        </Notice>
      ) : null}
    </div>
  );
}

export default function QuotationSandwichGeneratorWidget() {
  const strings = useToolUi(QUOTATION_SANDWICH_GENERATOR_STRINGS);
  const [input, setInput] = useState<SandwichInput>(EMPTY_INPUT);
  const [showLabels, setShowLabels] = useState(true);
  const set =
    <K extends keyof SandwichInput>(key: K) =>
    (value: SandwichInput[K]) =>
      setInput((previous) => ({ ...previous, [key]: value }));
  const sandwiches = buildSandwiches(input, strings);
  const hasAuthor = input.authors.trim() !== "";
  const area = (
    key: "leadIn" | "claim" | "meaning" | "suggests" | "why",
    label: string,
    placeholder: string,
    rows = 2
  ) => (
    <TextAreaField
      id={`qs-${key}`}
      label={label}
      onChange={set(key)}
      placeholder={placeholder}
      prose
      rows={rows}
      value={input[key]}
    />
  );

  return (
    <>
      <div className={`${CARD} space-y-5`}>
        <div className="grid gap-5 sm:grid-cols-2">
          <PillGroup
            label={strings.styleLabel}
            name="qs-style"
            onChange={set("style")}
            options={SANDWICH_STYLES.map((value) => ({
              value,
              label: strings.styles[value],
            }))}
            value={input.style}
          />
          <PillGroup
            label={strings.modeLabel}
            name="qs-mode"
            onChange={set("mode")}
            options={SANDWICH_MODES.map((value) => ({
              value,
              label: strings.modes[value],
            }))}
            value={input.mode}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <TextAreaField
            hint={strings.authorsHint}
            id="qs-authors"
            label={strings.authorsLabel}
            onChange={set("authors")}
            placeholder={strings.authorsPlaceholder}
            rows={2}
            value={input.authors}
          />
          {input.style === "apa" ? (
            <TextField
              id="qs-year"
              label={strings.yearLabel}
              onChange={set("year")}
              placeholder={strings.yearPlaceholder}
              value={input.year}
            />
          ) : null}
          <TextField
            hint={strings.pageHint}
            id="qs-page"
            label={strings.pageLabel}
            onChange={set("page")}
            placeholder={strings.pagePlaceholder}
            value={input.page}
          />
        </div>
        <TextAreaField
          id="qs-quote"
          label={strings.quoteLabel}
          onChange={set("quote")}
          placeholder={strings.quotePlaceholder}
          prose
          rows={3}
          value={input.quote}
        />
        {input.mode === "character" ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id="qs-speaker"
              label={strings.speakerLabel}
              onChange={set("speaker")}
              placeholder={strings.speakerPlaceholder}
              value={input.speaker}
            />
            <SelectField
              id="qs-verb"
              label={strings.verbLabel}
              onChange={set("verb")}
              options={strings.characterVerbs.map((value) => ({
                value,
                label: value,
              }))}
              value={input.verb}
            />
          </div>
        ) : (
          <SelectField
            id="qs-stance"
            label={strings.stanceLabel}
            onChange={set("stance")}
            options={SIGNAL_STANCES.map((value) => ({
              value,
              label: strings.stances[value],
            }))}
            value={input.stance}
          />
        )}
        {area("leadIn", strings.leadInLabel, strings.leadInPlaceholder)}
        <div className="space-y-4 border-border border-t pt-5">
          <p className="font-medium text-foreground text-sm">
            {strings.explainHeading}
          </p>
          <p className={HINT}>{strings.explainIntro}</p>
          {area("claim", strings.claimLabel, strings.claimPlaceholder)}
          {area("meaning", strings.meaningLabel, strings.meaningPlaceholder)}
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id="qs-keyword"
              label={strings.keywordLabel}
              onChange={set("keyword")}
              placeholder={strings.keywordPlaceholder}
              value={input.keyword}
            />
            <TextField
              id="qs-suggests"
              label={strings.suggestsLabel}
              onChange={set("suggests")}
              placeholder={strings.suggestsPlaceholder}
              value={input.suggests}
            />
          </div>
          {area("why", strings.whyLabel, strings.whyPlaceholder)}
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setInput({ ...strings.example })}
            type="button"
          >
            {strings.tryExample}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setInput(EMPTY_INPUT)}
            type="button"
          >
            {strings.clear}
          </button>
        </div>
      </div>

      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.resultHeading}</h2>
        {input.quote.trim() === "" ? (
          <Notice>{strings.needQuote}</Notice>
        ) : null}
        {input.quote.trim() !== "" && !hasAuthor ? (
          <Notice>{strings.needAuthor}</Notice>
        ) : null}
        {sandwiches.length > 0 && hasAuthor ? (
          <>
            <p className="text-muted-foreground text-sm">
              {strings.resultIntro}
            </p>
            <Checkbox
              checked={showLabels}
              label={strings.showLabels}
              onChange={setShowLabels}
            />
            {sandwiches.map((sandwich, index) => (
              <SandwichCard
                index={index}
                key={sandwich.id}
                sandwich={sandwich}
                showLabels={showLabels}
                strings={strings}
              />
            ))}
            <LengthCheck input={input} strings={strings} />
          </>
        ) : null}
      </section>
    </>
  );
}
